/**
 * TOP-500 — árbol Rubro → Grupo → Subgrupo con los productos de cada subgrupo.
 *
 * El servidor devuelve el árbol PLANO: una fila por combinación (rubro, grupo,
 * subgrupo) con sus contadores. La jerarquía se arma acá, en memoria, con
 * `buildHierarchy`; son unas 800 filas, así que no hace falta nada más.
 *
 * Selección: el usuario marca rubros, grupos, subgrupos y productos sueltos. Lo que se
 * guarda son SIEMPRE las hojas (los subgrupos): marcar un rubro marca todas sus hojas,
 * y así una casilla de padre puede quedar en "algunos" sin estados contradictorios.
 * `selectionNodes` vuelve a comprimir esa lista antes de mandarla al servidor: si están
 * todas las hojas de un rubro, se manda el rubro solo (el servidor acepta como mucho
 * 200 ramas por pedido, y hay más de 700 subgrupos).
 *
 * Un nivel vacío viaja como texto vacío, NUNCA como nulo: el servidor lee un nulo como
 * "todo lo que cuelga de este nivel" y un texto vacío como "las filas sin ese nivel".
 * Mandar nulo por una hoja sin grupo seleccionaría el rubro entero.
 *
 * Los productos de un subgrupo se piden al abrirlo (`GET /node`, paginado). El texto del
 * buscador se aplica a los subgrupos abiertos: el servidor busca dentro de un nodo, no
 * sobre la planilla entera.
 *
 * Lo leído se guarda bajo `planilla:cuenta:subgrupo` y además se descarta la respuesta
 * que llega después de un cambio de planilla o de cuenta. Sin las dos cosas, una
 * respuesta lenta pedida para la cuenta anterior se pintaba como si fuera de la cuenta
 * nueva y encima quedaba guardada, así que al abrir el subgrupo ya no se volvía a pedir.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { api } from '../../../api/client';
import { usePagination } from '../../../hooks/usePagination';
import { ProductRow, esSeleccionable, type Top500Product } from './ProductRow';

/** Una fila del árbol tal como la devuelve `GET /publisher/top500/tree`. */
export interface Top500Node {
  vertical: string;
  agg2: string | null;
  agg3: string | null;
  total: number;
  con_asin: number;
  cargados: number;
  sin_resolver: number;
}

/** Rama que se le manda al servidor al encolar. */
export interface SelectionNode {
  vertical: string;
  agg2?: string;
  agg3?: string;
}

interface Counts {
  total: number;
  con_asin: number;
  cargados: number;
}

export interface LeafNode extends Counts {
  key: string;
  label: string;
  node: Top500Node;
}

export interface GroupNode extends Counts {
  key: string;
  label: string;
  agg2: string | null;
  leaves: LeafNode[];
}

export interface VerticalNode extends Counts {
  key: string;
  label: string;
  vertical: string;
  groups: GroupNode[];
}

/** Separador de la clave de nodo. No aparece en los textos de la planilla. */
const SEP = '|';

/**
 * Tope del servidor de filas por página (`tree.ts`). Pedir más NO trae más: el servidor
 * recorta a 200 y pagina con 200. El selector ofrecía 300 y las páginas se contaban con
 * 300, así que en un subgrupo grande las últimas filas quedaban inalcanzables — y lo que
 * no se puede ver no se puede elegir.
 */
const MAX_PER_PAGE = 200;

/**
 * Marca de "ese nivel no existe" dentro de la clave. Un nivel nulo y un nivel con texto
 * vacío son filas DISTINTAS en la base, así que no pueden compartir clave: si las dos
 * cayeran en `rubro||`, marcar una marcaría la otra.
 */
const NULO = '∅';

/** Un nivel dentro de la clave: nulo y vacío se distinguen. PURA. */
function nivelClave(v: string | null): string {
  return v === null ? NULO : v;
}

/** Clave estable de una hoja del árbol: `rubro|grupo|subgrupo`. PURA. */
export function nodeKey(vertical: string, agg2: string | null, agg3: string | null): string {
  return `${vertical}${SEP}${nivelClave(agg2)}${SEP}${nivelClave(agg3)}`;
}

/**
 * Valor de un nivel para el servidor: un nivel ausente viaja como texto vacío.
 * Un nulo significaría "todo lo que cuelga", que es otra cosa. PURA.
 */
function levelParaApi(v: string | null): string {
  return v ?? '';
}

function sumar(dest: Counts, n: Top500Node): void {
  dest.total += n.total;
  dest.con_asin += n.con_asin;
  dest.cargados += n.cargados;
}

/**
 * Arma la jerarquía Rubro → Grupo → Subgrupo a partir de las filas planas, sumando los
 * contadores hacia arriba. Conserva el orden en que vinieron (el servidor ya las manda
 * ordenadas por rubro, grupo y subgrupo). PURA.
 */
export function buildHierarchy(nodes: Top500Node[], sinNombre: string): VerticalNode[] {
  const verticals: VerticalNode[] = [];
  const porVertical = new Map<string, VerticalNode>();
  const porGrupo = new Map<string, GroupNode>();

  for (const n of nodes) {
    let v = porVertical.get(n.vertical);
    if (!v) {
      v = {
        key: n.vertical,
        label: n.vertical || sinNombre,
        vertical: n.vertical,
        groups: [],
        total: 0,
        con_asin: 0,
        cargados: 0,
      };
      porVertical.set(n.vertical, v);
      verticals.push(v);
    }
    sumar(v, n);

    const gKey = `${n.vertical}${SEP}${nivelClave(n.agg2)}`;
    let g = porGrupo.get(gKey);
    if (!g) {
      g = {
        key: gKey,
        label: n.agg2 || sinNombre,
        agg2: n.agg2,
        leaves: [],
        total: 0,
        con_asin: 0,
        cargados: 0,
      };
      porGrupo.set(gKey, g);
      v.groups.push(g);
    }
    sumar(g, n);

    g.leaves.push({
      key: nodeKey(n.vertical, n.agg2, n.agg3),
      label: n.agg3 || sinNombre,
      node: n,
      total: n.total,
      con_asin: n.con_asin,
      cargados: n.cargados,
    });
  }

  return verticals;
}

/**
 * Comprime la selección de hojas en la lista de ramas más corta posible: rubro entero
 * si están todas sus hojas, si no grupo entero, si no hoja por hoja. PURA.
 */
export function selectionNodes(
  verticals: VerticalNode[],
  selected: ReadonlySet<string>
): SelectionNode[] {
  const out: SelectionNode[] = [];
  for (const v of verticals) {
    const todasV =
      v.groups.length > 0 && v.groups.every((g) => g.leaves.every((l) => selected.has(l.key)));
    if (todasV) {
      out.push({ vertical: v.vertical });
      continue;
    }
    for (const g of v.groups) {
      const todasG = g.leaves.length > 0 && g.leaves.every((l) => selected.has(l.key));
      if (todasG) {
        out.push({ vertical: v.vertical, agg2: levelParaApi(g.agg2) });
        continue;
      }
      for (const l of g.leaves) {
        if (!selected.has(l.key)) continue;
        out.push({
          vertical: v.vertical,
          agg2: levelParaApi(l.node.agg2),
          agg3: levelParaApi(l.node.agg3),
        });
      }
    }
  }
  return out;
}

/** Casilla de tres estados: marcada, sin marcar, o "algunos de abajo". */
function TriCheckbox({
  checked,
  indeterminate,
  onChange,
  label,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: (v: boolean) => void;
  /** Qué elige esta casilla, para quien usa lector de pantalla. */
  label: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      onClick={(e) => e.stopPropagation()}
      aria-label={label}
      className="flex-shrink-0"
    />
  );
}

/** Contadores de un nodo: total · con producto de Amazon · ya cargados (en gris). */
function Counters({ c }: { c: Counts }) {
  const { t } = useTranslation();
  return (
    <span className="text-[11px] text-[var(--color-text-secondary)] whitespace-nowrap">
      {c.total}
      {' · '}
      {t('pub.top500.withAsin', { n: c.con_asin })}
      {' · '}
      <span className="text-[var(--color-text-tertiary)]">
        {t('pub.top500.alreadyLoaded', { n: c.cargados })}
      </span>
    </span>
  );
}

/** Lo que sabemos de los productos de un subgrupo abierto. */
interface LeafData {
  items: Top500Product[];
  total: number;
  page: number;
  loading: boolean;
  error: string | null;
}

interface NodeResponse {
  items: Top500Product[];
  total: number;
}

interface TreeProps {
  sheetId: number;
  sellerId: number;
  /** Texto del buscador, ya con su espera de 400 ms hecha arriba. */
  search: string;
  verticals: VerticalNode[];
  selectedNodes: ReadonlySet<string>;
  /** Marca o desmarca varias hojas de una (un rubro son todas las suyas). */
  onToggleLeaves: (keys: string[], selected: boolean) => void;
  selectedIds: ReadonlySet<number>;
  onToggleId: (id: number, selected: boolean) => void;
  /** Sube de número cuando hay que releer los subgrupos abiertos (después de encolar). */
  reloadToken: number;
  canMoney: boolean;
  sellerNames: Map<number, string>;
}

export function Tree({
  sheetId,
  sellerId,
  search,
  verticals,
  selectedNodes,
  onToggleLeaves,
  selectedIds,
  onToggleId,
  reloadToken,
  canMoney,
  sellerNames,
}: TreeProps) {
  const { t } = useTranslation();
  const { perPage, setPerPage, PAGE_SIZE_OPTIONS } = usePagination('pub-top500');
  // Lo guardado puede ser 300 de antes: se usa 200 para pedir Y para contar páginas.
  const porPagina = Math.min(perPage, MAX_PER_PAGE);
  const opcionesPorPagina = PAGE_SIZE_OPTIONS.filter((n) => n <= MAX_PER_PAGE);

  const [openVerticals, setOpenVerticals] = useState<ReadonlySet<string>>(new Set<string>());
  const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(new Set<string>());
  const [openLeaves, setOpenLeaves] = useState<ReadonlySet<string>>(new Set<string>());
  const [leafData, setLeafData] = useState<Record<string, LeafData>>({});

  /**
   * Clave con la que se guardan los productos leídos: incluye planilla y cuenta. Así una
   * respuesta que llega tarde, pedida para la cuenta anterior, cae en otra casilla y NO
   * se pinta ni queda como dato bueno del subgrupo que ahora se está mirando.
   */
  const dataKey = useCallback(
    (leafKey: string) => `${sheetId}:${sellerId}:${leafKey}`,
    [sheetId, sellerId]
  );

  /** Planilla y cuenta que se están mirando AHORA, para descartar respuestas vencidas. */
  const vigente = useRef({ sheetId, sellerId });
  vigente.current = { sheetId, sellerId };

  const loadLeaf = useCallback(
    async (leaf: LeafNode, page: number) => {
      const key = dataKey(leaf.key);
      setLeafData((prev) => ({
        ...prev,
        [key]: {
          items: prev[key]?.items ?? [],
          total: prev[key]?.total ?? 0,
          page,
          loading: true,
          error: null,
        },
      }));

      const qs = new URLSearchParams({
        sheet_id: String(sheetId),
        seller_id: String(sellerId),
        vertical: leaf.node.vertical,
        agg2: levelParaApi(leaf.node.agg2),
        agg3: levelParaApi(leaf.node.agg3),
        page: String(page),
        per_page: String(porPagina),
      });
      if (search.trim()) qs.set('search', search.trim());

      // La llamada no puede romper la pantalla: un corte de red o una respuesta que no
      // sea JSON (un servidor viejo devuelve una página de error) se muestra como aviso.
      let res: { success: boolean; data?: NodeResponse; error?: string };
      try {
        res = await api.get<NodeResponse>(`/publisher/top500/node?${qs.toString()}`);
      } catch (err) {
        res = { success: false, error: err instanceof Error ? err.message : String(err) };
      }

      // Respuesta vencida: mientras se pedía, el usuario cambió de planilla o de cuenta.
      // No se pinta ni se guarda (la clave ya la separa, pero ir y volver a la misma
      // cuenta dejaría entrar contenido viejo por esa misma clave).
      if (vigente.current.sheetId !== sheetId || vigente.current.sellerId !== sellerId) return;

      setLeafData((prev) => ({
        ...prev,
        [key]: {
          items: res.success && res.data ? res.data.items : (prev[key]?.items ?? []),
          total: res.success && res.data ? res.data.total : (prev[key]?.total ?? 0),
          page,
          loading: false,
          error: res.success ? null : res.error || t('pub.top500.nodeError'),
        },
      }));
    },
    [sheetId, sellerId, porPagina, search, t, dataKey]
  );

  // Índice hoja por clave y copia viva de lo abierto: los usa el efecto de recarga sin
  // meterlos en sus dependencias (si no, abrir un subgrupo recargaría todos los demás).
  const leafByKey = useMemo(() => {
    const m = new Map<string, LeafNode>();
    for (const v of verticals) for (const g of v.groups) for (const l of g.leaves) m.set(l.key, l);
    return m;
  }, [verticals]);
  const openRef = useRef(openLeaves);
  openRef.current = openLeaves;
  const leafRef = useRef(leafByKey);
  leafRef.current = leafByKey;

  // Otra planilla o cuenta: lo abierto y lo leído ya no corresponde. Va ANTES del
  // efecto de recarga para que éste no pida los subgrupos de la planilla anterior.
  useEffect(() => {
    setOpenVerticals(new Set<string>());
    setOpenGroups(new Set<string>());
    setOpenLeaves(new Set<string>());
    setLeafData({});
    openRef.current = new Set<string>();
  }, [sheetId, sellerId]);

  // Cambió el buscador, el tamaño de página o se acaba de encolar: los subgrupos
  // abiertos se releen desde la primera página.
  useEffect(() => {
    for (const key of openRef.current) {
      const leaf = leafRef.current.get(key);
      if (leaf) void loadLeaf(leaf, 1);
    }
  }, [loadLeaf, reloadToken]);

  const toggleOpen = (
    set: ReadonlySet<string>,
    apply: (s: ReadonlySet<string>) => void,
    key: string
  ) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    apply(next);
  };

  const toggleLeafOpen = (leaf: LeafNode) => {
    const abierto = openLeaves.has(leaf.key);
    toggleOpen(openLeaves, setOpenLeaves, leaf.key);
    // Solo cuenta lo leído para ESTA planilla y ESTA cuenta.
    if (!abierto && !leafData[dataKey(leaf.key)]) void loadLeaf(leaf, 1);
  };

  const leavesOf = (n: VerticalNode | GroupNode | LeafNode): string[] => {
    if ('groups' in n) return n.groups.flatMap((g) => g.leaves.map((l) => l.key));
    if ('leaves' in n) return n.leaves.map((l) => l.key);
    return [n.key];
  };

  const estado = (keys: string[]): { checked: boolean; some: boolean } => {
    let marcadas = 0;
    for (const k of keys) if (selectedNodes.has(k)) marcadas++;
    return { checked: keys.length > 0 && marcadas === keys.length, some: marcadas > 0 };
  };

  if (verticals.length === 0) {
    return (
      <p className="text-xs text-[var(--color-text-secondary)] px-3 py-4">
        {t('pub.top500.noNodes')}
      </p>
    );
  }

  return (
    <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg">
      <div className="flex items-center justify-end gap-2 px-3 py-2 border-b border-[var(--color-border)]">
        <span className="text-[11px] text-[var(--color-text-tertiary)]">
          {t('pub.top500.perPage')}
        </span>
        <select
          value={porPagina}
          onChange={(e) => setPerPage(Number(e.target.value))}
          className="px-2 py-0.5 text-[11px] rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)]"
        >
          {opcionesPorPagina.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>

      <div className="divide-y divide-[var(--color-border)]">
        {verticals.map((v) => {
          const vKeys = leavesOf(v);
          const vEstado = estado(vKeys);
          const vAbierto = openVerticals.has(v.key);
          return (
            <div key={v.key}>
              <div
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
                onClick={() => toggleOpen(openVerticals, setOpenVerticals, v.key)}
              >
                {vAbierto ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <TriCheckbox
                  checked={vEstado.checked}
                  indeterminate={vEstado.some}
                  onChange={(s) => onToggleLeaves(vKeys, s)}
                  label={t('pub.top500.selectNodeLabel', { name: v.label })}
                />
                <span className="text-xs font-semibold text-[var(--color-text)] flex-1 truncate">
                  {v.label}
                </span>
                <Counters c={v} />
              </div>

              {vAbierto &&
                v.groups.map((g) => {
                  const gKeys = leavesOf(g);
                  const gEstado = estado(gKeys);
                  const gAbierto = openGroups.has(g.key);
                  return (
                    <div key={g.key}>
                      <div
                        className="flex items-center gap-2 pl-8 pr-3 py-1.5 cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
                        onClick={() => toggleOpen(openGroups, setOpenGroups, g.key)}
                      >
                        {gAbierto ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        <TriCheckbox
                          checked={gEstado.checked}
                          indeterminate={gEstado.some}
                          onChange={(s) => onToggleLeaves(gKeys, s)}
                          label={t('pub.top500.selectNodeLabel', { name: g.label })}
                        />
                        <span className="text-xs text-[var(--color-text)] flex-1 truncate">
                          {g.label}
                        </span>
                        <Counters c={g} />
                      </div>

                      {gAbierto &&
                        g.leaves.map((l) => {
                          const lEstado = estado([l.key]);
                          const lAbierto = openLeaves.has(l.key);
                          const data = leafData[dataKey(l.key)];
                          const paginas = data ? Math.max(1, Math.ceil(data.total / porPagina)) : 1;
                          return (
                            <div key={l.key}>
                              <div
                                className="flex items-center gap-2 pl-14 pr-3 py-1.5 cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
                                onClick={() => toggleLeafOpen(l)}
                              >
                                {lAbierto ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                <TriCheckbox
                                  checked={lEstado.checked}
                                  indeterminate={false}
                                  onChange={(s) => onToggleLeaves([l.key], s)}
                                  label={t('pub.top500.selectNodeLabel', { name: l.label })}
                                />
                                <span className="text-[11px] text-[var(--color-text-secondary)] flex-1 truncate">
                                  {l.label}
                                </span>
                                <Counters c={l} />
                              </div>

                              {lAbierto && (
                                <div className="pl-14 pr-3 pb-2">
                                  {data?.loading && (
                                    <div className="flex items-center gap-2 py-3 text-[11px] text-[var(--color-text-secondary)]">
                                      <Loader2 size={12} className="animate-spin" />
                                      {t('common.loading')}
                                    </div>
                                  )}

                                  {data?.error && (
                                    <p className="py-2 text-[11px] text-[var(--color-error)]">
                                      {data.error}
                                    </p>
                                  )}

                                  {data && !data.loading && !data.error && data.items.length === 0 && (
                                    <p className="py-2 text-[11px] text-[var(--color-text-secondary)]">
                                      {t('pub.top500.noProducts')}
                                    </p>
                                  )}

                                  {data && data.items.length > 0 && (
                                    <div className="border border-[var(--color-border)] rounded bg-[var(--color-bg)]">
                                      {data.items.map((p) => (
                                        <ProductRow
                                          key={p.id}
                                          product={p}
                                          selected={selectedIds.has(p.id)}
                                          onToggle={onToggleId}
                                          canMoney={canMoney}
                                          sellerNames={sellerNames}
                                        />
                                      ))}
                                    </div>
                                  )}

                                  {data && data.total > porPagina && (
                                    <div className="flex items-center justify-end gap-2 pt-2 text-[11px] text-[var(--color-text-secondary)]">
                                      <button
                                        disabled={data.page <= 1 || data.loading}
                                        onClick={() => void loadLeaf(l, data.page - 1)}
                                        className="px-2 py-0.5 rounded border border-[var(--color-border)] disabled:opacity-40"
                                      >
                                        {t('common.previous')}
                                      </button>
                                      <span>
                                        {t('pub.top500.page', { page: data.page, pages: paginas })}
                                      </span>
                                      <button
                                        disabled={data.page >= paginas || data.loading}
                                        onClick={() => void loadLeaf(l, data.page + 1)}
                                        className="px-2 py-0.5 rounded border border-[var(--color-border)] disabled:opacity-40"
                                      >
                                        {t('common.next')}
                                      </button>
                                    </div>
                                  )}

                                  {data && !data.loading && !data.error && (
                                    <p className="pt-1 text-[10px] text-[var(--color-text-tertiary)]">
                                      {t('pub.top500.selectableHint', {
                                        n: data.items.filter(esSeleccionable).length,
                                      })}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  );
                })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
